import React from 'react';
import { useParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Button, Table } from 'react-bootstrap';
import Loader from '../components/Loader';
import Message from '../components/Message';
import {
  useGetBenchmarkByIdQuery,
  useDeleteBenchmarkMutation,
  useDeleteMyBenchmarkMutation,
} from '../slices/benchmarksApiSlice';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { toast } from 'react-toastify';
import { usePalette } from '../hooks/usePalette';
import { useListScope, getListScope, SCOPE_ALL } from '../hooks/useListScope';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const LOCAL_TIME_FORMAT = { dateStyle: 'medium', timeStyle: 'medium' };

function formatUtcOffset(minutes) {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
}

// Sources whose stored instant is exact, so converting it to the tester's
// IANA zone is safe. 'estimated' instants are guesses and 'unknown' values are
// wall-clock times stored as if UTC — converting those would shift the time.
const EXACT_OFFSET_SOURCES = new Set(['client', 'zone']);

// Start time as the tester saw it: prefer the client's IANA zone, else the
// stored UTC offset (estimated for older clients). Null when neither is known.
function formatTesterLocalTime(benchmark) {
  const start = new Date(benchmark.startTimeString);
  if (Number.isNaN(start.getTime())) return null;
  const source = benchmark.utcOffsetSource;

  if (benchmark.timeZone && EXACT_OFFSET_SOURCES.has(source)) {
    try {
      const time = start.toLocaleString([], { ...LOCAL_TIME_FORMAT, timeZone: benchmark.timeZone });
      const zoneAbbr = new Intl.DateTimeFormat([], { timeZone: benchmark.timeZone, timeZoneName: 'short' })
        .formatToParts(start)
        .find((part) => part.type === 'timeZoneName')?.value;
      return `${time}${zoneAbbr ? ` ${zoneAbbr}` : ''} (${benchmark.timeZone})`;
    } catch {
      // Zone unknown to this browser — fall back to the offset
    }
  }

  // start + offset is the tester's wall clock. For 'estimated' the wall clock
  // is exact; only the offset (and so the instant) is a guess.
  if (typeof benchmark.utcOffsetMinutes === 'number' && source !== 'unknown') {
    const shifted = new Date(start.getTime() + benchmark.utcOffsetMinutes * 60 * 1000);
    const time = shifted.toLocaleString([], { ...LOCAL_TIME_FORMAT, timeZone: 'UTC' });
    const estimated = source === 'estimated' ? ' (estimated)' : '';
    return `${time} ${formatUtcOffset(benchmark.utcOffsetMinutes)}${estimated}`;
  }

  // Wall clock was stored as if UTC; show it unconverted
  if (source === 'unknown') {
    const time = start.toLocaleString([], { ...LOCAL_TIME_FORMAT, timeZone: 'UTC' });
    return `${time} (UTC offset unknown)`;
  }

  return null;
}

// scope='all'  → breadcrumb back to All Benchmarks
// scope='mine' → breadcrumb back to My Benchmarks (owner only)
export default function BenchmarkDetailsScreen({ scope = SCOPE_ALL }) {
  const { palette } = usePalette();

  const { userInfo } = useSelector((state) => state.auth);
  const { benchmarkId } = useParams();
  const listScope = useListScope(scope);

  const safeUserId = userInfo?._id ?? undefined;

  const {
    data: benchmark,
    isLoading,
    error,
  } = useGetBenchmarkByIdQuery(
    safeUserId ? { userId: safeUserId, benchmarkId } : { benchmarkId },
  );

  const navigate = useNavigate();

  const [deleteBenchmark] = useDeleteBenchmarkMutation();
  const [deleteMyBenchmark] = useDeleteMyBenchmarkMutation();

  const deleteMyHandler = async (benchmarkId) => {
    if (
      window.confirm(
        `Are you sure you want to delete Benchmark ${benchmarkId}? This CANNOT be undone.`,
      )
    ) {
      try {
        await deleteMyBenchmark({ benchmarkId }).unwrap();
        toast.success(`Benchmark ${benchmarkId} deleted`);
        navigate(`/me/benchmarks`);
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const deleteHandler = async (benchmarkId) => {
    if (
      window.confirm(
        `Are you sure you want to delete Benchmark ${benchmarkId}? This CANNOT be undone.`,
      )
    ) {
      try {
        await deleteBenchmark({ userId: userInfo._id, benchmarkId });
        toast.success(`Benchmark ${benchmarkId} deleted`);
        navigate('/');
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  if (isLoading) return <Loader />;

  if (error)
    return (
      <Message variant='danger'>{error?.data?.message || error.error}</Message>
    );

  // Not the user's benchmark — show the public benchmark page instead
  if (listScope.mine && String(benchmark.userId) !== String(safeUserId)) {
    return <Navigate to={getListScope(SCOPE_ALL).benchmarkPath(benchmarkId)} replace />;
  }

  var wOp = null;
  var rOp = null;

  var sampleNum = 0;

  // new refactor vs pre refactor...
  const btype = benchmark.config?.benchmarkType ?? benchmark.benchmarkType;

  switch (btype) {
    case 'WRITE':
      wOp = benchmark.operations?.[0] ?? null;
      sampleNum = wOp?.numSamples ?? 0;
      break;
    case 'READ':
      rOp = benchmark.operations?.[0] ?? null;
      sampleNum = rOp?.numSamples ?? 0;
      break;
    case 'READ_WRITE':
      wOp = benchmark.operations?.[0] ?? null;
      rOp = benchmark.operations?.[1] ?? null;
      sampleNum = wOp?.numSamples ?? 0;
      break;
    default:
      wOp = null;
      rOp = null;
      break;
  }

  var rDatasets = [];
  var wDatasets = [];
  var wChartData = {};
  var rChartData = {};

  var w_average = -1;
  var w_average_access = -1;
  var w_operation_iops = -1;

  if (wOp !== null) {
    const samples = wOp.samples ?? [];

    const write = samples.map((s) => ({
      x: s.sn,
      y: s.bw,
    }));
    write.sort((a, b) => a.x - b.x);

    const writeAvg = samples.map((s) => ({
      x: s.sn,
      y: s.bt,
    }));
    writeAvg.sort((a, b) => a.x - b.x);

    const writeMin = samples.map((s) => ({
      x: s.sn,
      y: s.mn,
    }));
    writeMin.sort((a, b) => a.x - b.x);

    const writeMax = samples.map((s) => ({
      x: s.sn,
      y: s.mx,
    }));
    writeMax.sort((a, b) => a.x - b.x);

    const writeAccess = samples.map((s) => ({
      x: s.sn,
      y: s.lt,
    }));
    writeAccess.sort((a, b) => a.x - b.x);

    w_average = wOp.bandwidth;
    w_average_access = wOp.latency;
    w_operation_iops = wOp.iops;

    wDatasets.push(
      {
        label: 'Write Sample',
        data: write,
        borderColor: palette.writeSample,
        yAxisID: 'y',
        xAxisID: 'x',
        pointRadius: 0,
      },
      {
        label: 'Write Trend',
        data: writeAvg,
        borderColor: palette.writeTrend,
        yAxisID: 'y',
        parsing: false,
        showLine: true,
        pointRadius: 0,
      },
      {
        label: 'Write Min',
        data: writeMin,
        borderColor: palette.writeMin,
        yAxisID: 'y',
        xAxisID: 'x',
        showLine: true,
        pointRadius: 0,
        hidden: true,
      },
      {
        label: 'Write Max',
        data: writeMax,
        borderColor: palette.writeMax,
        yAxisID: 'y',
        xAxisID: 'x',
        pointRadius: 0,
        hidden: true,
      },
      {
        label: 'Write Latency',
        data: writeAccess,
        showLine: false,
        yAxisID: 'y1',
        xAxisID: 'x',
        pointStyle: 'rect', // Changes the shape to a rectangle/square
        pointRadius: 6, // Makes the square large enough to see
        pointHoverRadius: 8, // Slightly bigger on hover
        backgroundColor: palette.writeLatency,
        borderColor: palette.writeLatency,
        pointBackgroundColor: palette.writeLatency,
      },
    );

    wChartData = {
      datasets: wDatasets,
    };
  }

  var r_average = -1;
  var r_average_access = -1;
  var r_operation_iops = -1;

  if (rOp !== null) {
    const samples = rOp.samples ?? [];

    const read = samples.map((s) => ({
      x: s.sn,
      y: s.bw,
    }));
    read.sort((a, b) => a.x - b.x);

    const readAvg = samples.map((s) => ({
      x: s.sn,
      y: s.bt,
    }));
    readAvg.sort((a, b) => a.x - b.x);

    const readMin = samples.map((s) => ({
      x: s.sn,
      y: s.mn,
    }));
    readMin.sort((a, b) => a.x - b.x);

    const readMax = samples.map((s) => ({
      x: s.sn,
      y: s.mx,
    }));
    readMax.sort((a, b) => a.x - b.x);

    const readAccess = samples.map((s) => ({
      x: s.sn,
      y: s.lt,
    }));
    readAccess.sort((a, b) => a.x - b.x);

    r_average = rOp.bandwidth;
    r_average_access = rOp.latency;
    r_operation_iops = rOp.iops;

    rDatasets.push(
      {
        label: 'Read Sample',
        data: read,
        borderColor: palette.readSample,
        tension: 0.2,
        yAxisID: 'y',
        xAxisID: 'x',
        pointRadius: 0,
      },
      {
        label: 'Read Trend',
        data: readAvg,
        borderColor: palette.readTrend,
        yAxisID: 'y',
        xAxisID: 'x',
        pointRadius: 0,
      },
      {
        label: 'Read Min',
        data: readMin,
        borderColor: palette.readMin,
        yAxisID: 'y',
        xAxisID: 'x',
        pointRadius: 0,
        hidden: true,
      },
      {
        label: 'Read Max',
        data: readMax,
        borderColor: palette.readMax,
        yAxisID: 'y',
        xAxisID: 'x',
        pointRadius: 0,
        hidden: true,
      },
      {
        label: 'Read Latency',
        data: readAccess,
        pointBackgroundColor: palette.readLatency,
        pointColor: palette.readLatency,
        borderColor: palette.readLatency,
        backgroundColor: palette.readLatency,
        showLine: false,
        yAxisID: 'y1',
        xAxisID: 'x',
        pointStyle: 'rect', // Changes the shape to a rectangle/square
        pointRadius: 6, // Makes the square large enough to see
        pointHoverRadius: 8, // Slightly bigger on hover
      },
    );

    rChartData = {
      datasets: rDatasets,
    };
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allows us to control height via CSS/Bootstrap
    plugins: {
      legend: {
        labels: {
          color: palette.textColor,
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor,
              strokeStyle: dataset.borderColor,
              lineWidth: 1,
              hidden: !chart.isDatasetVisible(i),
              index: i,
              datasetIndex: i,
              // Ensure the text color matches the theme
              fontColor: palette.textColor, // Compat with older versions
              color: palette.textColor, // Standard for current v3/v4
              // Only use point symbol for Latency series
              usePointStyle: dataset.label.includes('Latency'),
              pointStyle: dataset.pointStyle,
            }));
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: palette.gridColor,
          borderDash: [5, 5],
        },
        type: 'linear',
        min: 0,
        max: sampleNum,

        ticks: {
          color: palette.textColor,
        },
        title: {
          display: true,
          text: 'Sample',
          color: palette.textColor,
        },
      },
      y: {
        grid: {
          color: palette.gridColor,
          borderDash: [5, 5],
        },
        type: 'linear',
        position: 'left',
        title: {
          display: true,
          text: 'Bandwidth (MB/s)',
          color: palette.textColor,
        },
        ticks: {
          color: palette.textColor,
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: {
          display: true,
          text: 'Access Time (ms)',
          color: palette.textColor,
        },
        ticks: {
          color: palette.textColor,
        },
      },
    },
  };

  return (
    <>
      {/* ── Panel 1: Breadcrumb Navigation ── */}
      <Card className='shadow-sm card--opaque my-3'>
        <Card.Header className='fw-bold'>
          <nav aria-label='breadcrumb'>
            <ol className='breadcrumb mb-0'>
              <li className='breadcrumb-item fw-bold'>
                <Link
                  to={listScope.benchmarksPath}
                  className='breadcrumb-link--nav'
                >
                  {listScope.benchmarksLabel}
                </Link>
              </li>
              <li className='breadcrumb-item active fw-bold' aria-current='page'>
                {benchmark.driveInfo?.driveModel ?? 'Benchmark'} -{' '}
                {benchmark.driveInfo?.percentUsed ?? '--'}% (
                {benchmark.driveInfo?.usedGb ?? '--'}/
                {benchmark.driveInfo?.totalGb ?? '--'} GB)
              </li>
            </ol>
          </nav>
        </Card.Header>
      </Card>

      {/* ── Panel 2a: Read Benchmark ── */}
      {rOp !== null && (
        <Card className='shadow-sm card--opaque mb-3'>
          <Card.Header className='fw-bold'>Read Benchmark</Card.Header>
          <Card.Body>
            <Row className='gy-2 gx-0 align-items-stretch'>
              <Col xs={12} md={3} className='d-flex flex-column gap-2 pe-md-2'>
                <Card className='text-center shadow-sm card__bmrk--metric-switch flex-fill w-100'>
                  <Card.Body className='d-flex flex-column justify-content-center'>
                    <div className='small text-uppercase fw-bold'>
                      BANDWIDTH
                    </div>
                    <h4 className='my-2'>
                      {r_average !== -1 ? r_average : ' -- '} MB/s
                    </h4>
                  </Card.Body>
                </Card>
                <Card className='text-center shadow-sm card__bmrk--metric-switch flex-fill w-100'>
                  <Card.Body className='d-flex flex-column justify-content-center'>
                    <div className='small text-uppercase fw-bold'>
                      ACCESS TIME
                    </div>
                    <h4 className='my-2'>
                      {r_average_access !== -1
                        ? r_average_access
                        : ' -- '}{' '}
                      ms
                    </h4>
                  </Card.Body>
                </Card>
                <Card className='text-center shadow-sm card__bmrk--metric-switch flex-fill w-100'>
                  <Card.Body className='d-flex flex-column justify-content-center'>
                    <div className='small text-uppercase fw-bold'>
                      IOPS
                    </div>
                    <h4 className='my-2'>
                      {r_operation_iops !== -1
                        ? r_operation_iops
                        : ' -- '}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={9}>
                <div
                  className='h-100 rounded'
                  style={{
                    backgroundColor: palette.chartBg,
                    padding: '0.75rem',
                  }}
                >
                  <div style={{ height: '310px' }}>
                    <Line data={rChartData} options={chartOptions} />
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ── Panel 2b: Write Benchmark ── */}
      {wOp !== null && (
        <Card className='shadow-sm card--opaque mb-3'>
          <Card.Header className='fw-bold'>Write Benchmark</Card.Header>
          <Card.Body>
            <Row className='gy-2 gx-0 align-items-stretch'>
              <Col xs={12} md={3} className='d-flex flex-column gap-2 pe-md-2'>
                <Card className='text-center shadow-sm card__bmrk--metric-switch flex-fill w-100'>
                  <Card.Body className='d-flex flex-column justify-content-center'>
                    <div className='small text-uppercase fw-bold'>
                      BANDWIDTH
                    </div>
                    <h4 className='my-2'>
                      {w_average !== -1 ? w_average : ' -- '} MB/s
                    </h4>
                  </Card.Body>
                </Card>
                <Card className='text-center shadow-sm card__bmrk--metric-switch flex-fill w-100'>
                  <Card.Body className='d-flex flex-column justify-content-center'>
                    <div className='small text-uppercase fw-bold'>
                      ACCESS TIME
                    </div>
                    <h4 className='my-2'>
                      {w_average_access !== -1
                        ? w_average_access
                        : ' -- '}{' '}
                      ms
                    </h4>
                  </Card.Body>
                </Card>
                <Card className='text-center shadow-sm card__bmrk--metric-switch flex-fill w-100'>
                  <Card.Body className='d-flex flex-column justify-content-center'>
                    <div className='small text-uppercase fw-bold'>
                      IOPS
                    </div>
                    <h4 className='my-2'>
                      {w_operation_iops !== -1
                        ? w_operation_iops
                        : ' -- '}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={9}>
                <div
                  className='h-100 rounded'
                  style={{
                    backgroundColor: palette.chartBg,
                    padding: '0.75rem',
                  }}
                >
                  <div style={{ height: '310px' }}>
                    <Line data={wChartData} options={chartOptions} />
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ── Panel 3: Benchmark Details (stats table) ── */}
      <Card className='shadow-sm card--opaque mb-3'>
        <Card.Header className='fw-bold'>Benchmark Details</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Table borderless size='sm' className='mb-0'>
                <tbody>
                  {benchmark.config?.profile?.name && (
                    <tr>
                      <td className='fw-bold'>Profile</td>
                      <td>{benchmark.config.profile.name}</td>
                    </tr>
                  )}
                  {typeof benchmark.profileModified === 'boolean' && (
                    <tr>
                      <td className='fw-bold'>Profile Modified</td>
                      <td>{benchmark.profileModified ? 'Yes' : 'No'}</td>
                    </tr>
                  )}
                  <tr>
                    <td className='fw-bold'>Drive Model</td>
                    <td>{benchmark.driveInfo?.driveModel}</td>
                  </tr>
                  {benchmark.driveInfo?.driveInterface && (
                    <tr>
                      <td className='fw-bold'>Interface</td>
                      <td>
                        {benchmark.driveInfo.driveInterface}
                        {benchmark.driveInfo.driveInterfaceVersion && ` ${benchmark.driveInfo.driveInterfaceVersion}`}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className='fw-bold'>Benchmark Type</td>
                    <td>{benchmark.config?.benchmarkType}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Start Time</td>
                    <td>
                      {/* 'unknown' stores the tester's wall clock as if UTC — not a real
                          instant — so it can't be converted to the viewer's zone */}
                      {!benchmark.startTimeString
                        ? '—'
                        : benchmark.utcOffsetSource === 'unknown'
                          ? formatTesterLocalTime(benchmark)
                          : new Date(benchmark.startTimeString).toLocaleString([], {
                              dateStyle: 'medium',
                              timeStyle: 'long',
                            })}
                    </td>
                  </tr>
                  {benchmark.utcOffsetSource !== 'unknown' && formatTesterLocalTime(benchmark) && (
                    <tr>
                      <td className='fw-bold'>Tester's Local Time</td>
                      <td>{formatTesterLocalTime(benchmark)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className='fw-bold'>Duration</td>
                    <td>{benchmark.duration}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Operating System</td>
                    <td>{benchmark.systemInfo?.os}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Processor</td>
                    <td>{benchmark.systemInfo?.processorName}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Architecture Type</td>
                    <td>{benchmark.systemInfo?.arch}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <Table borderless size='sm' className='mb-0'>
                <tbody>
                  <tr>
                    <td className='fw-bold'>Used Capacity</td>
                    <td>{benchmark.driveInfo?.usedGb} GB</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Total Capacity</td>
                    <td>{benchmark.driveInfo?.totalGb} GB</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Percent Used</td>
                    <td>{benchmark.driveInfo?.percentUsed}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Block Order</td>
                    <td>{benchmark.config?.blockOrder}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Number of Blocks</td>
                    <td>{benchmark.config?.numBlocks}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Block Size</td>
                    <td>{benchmark.config?.blockSize}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>Number of Samples</td>
                    <td>{benchmark.config?.numSamples}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>txSize</td>
                    <td>{benchmark.config?.txSize}</td>
                  </tr>
                  <tr>
                    <td className='fw-bold'>jdk</td>
                    <td>{benchmark.systemInfo?.jdk}</td>
                  </tr>
                  {benchmark.config?.appVersion && (
                    <tr>
                      <td className='fw-bold'>Client Version</td>
                      <td>v{benchmark.config.appVersion}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {userInfo && userInfo.isAdmin && (
        <Card className='border-danger mt-3 shadow-sm mb-5'>
          <Card.Header className='bg-danger text-white fw-bold'>
            Admin Danger Zone
          </Card.Header>
          <Card.Body className='text-center p-4'>
            <p className='mb-4'>
              Warning: Deleting this benchmark is permanent and CANNOT be
              undone.
            </p>
            <Button
              variant='danger'
              size='lg'
              className='w-100 fw-bold py-3'
              onClick={() => deleteHandler(benchmark._id)}
            >
              DELETE
            </Button>
          </Card.Body>
        </Card>
      )}

      {userInfo && !userInfo.isAdmin && (benchmark.userId || benchmark.systemId) && (
        <Card className='border-danger mt-3 shadow-sm mb-5'>
          <Card.Header className='bg-danger text-white fw-bold'>
            Danger Zone
          </Card.Header>
          <Card.Body className='text-center p-4'>
            <p className='mb-4'>
              Warning: Deleting this benchmark is permanent and CANNOT be
              undone.
            </p>
            <Button
              variant='danger'
              size='lg'
              className='w-100 fw-bold py-3'
              onClick={() => deleteMyHandler(benchmark._id)}
            >
              DELETE
            </Button>
          </Card.Body>
        </Card>
      )}
    </>
  );
};